import {
  BACKGROUNDS,
  BACKGROUND_IDS,
  FONT_PAIRS,
  FONT_PAIR_IDS,
  PALETTES,
  PALETTE_IDS,
  backgroundBlend,
  backgroundLayers,
  scrimLayers,
  type ThemeChoice,
} from '@simplelaunch/renderer'

interface Props {
  readonly value: ThemeChoice
  readonly seed: string
  readonly onChange: (theme: ThemeChoice) => void
}

/**
 * The anti-bikeshedding surface.
 *
 * Every control is a fixed set of pre-vetted options -- no colour picker, no
 * font size, no spacing. A founder can try all 144 combinations in about
 * fifteen seconds and cannot produce an unreadable one, which is the entire
 * pitch.
 */
export function ThemePicker({ value, seed, onChange }: Props) {
  return (
    <>
      <fieldset>
        <legend>Palette</legend>
        <div className="row" role="group" aria-label="Palette">
          {PALETTE_IDS.map((id) => {
            const p = PALETTES[id]
            return (
              <button
                key={id}
                type="button"
                className="swatch"
                aria-pressed={value.palette === id}
                aria-label={p.label}
                title={p.label}
                onClick={() => onChange({ ...value, palette: id })}
              >
                <span className="pal">
                  <i style={{ background: p.bg }} />
                  <i style={{ background: p.accent }} />
                </span>
                <span className="pal-name">{p.label}</span>
              </button>
            )
          })}
        </div>
      </fieldset>

      <fieldset>
        <legend>Type</legend>
        <div className="row" role="group" aria-label="Typeface">
          {FONT_PAIR_IDS.map((id) => {
            const f = FONT_PAIRS[id]
            return (
              <button
                key={id}
                type="button"
                className="swatch font"
                aria-pressed={value.font === id}
                // The label doubles as the specimen, so the control shows what
                // it does rather than describing it.
                style={{ fontFamily: f.display, fontWeight: f.displayWeight }}
                onClick={() => onChange({ ...value, font: id })}
              >
                {f.label}
              </button>
            )
          })}
        </div>
      </fieldset>

      <fieldset>
        <legend>Backdrop</legend>
        <div className="row" role="group" aria-label="Backdrop">
          {BACKGROUND_IDS.map((id) => {
            const palette = PALETTES[value.palette]
            // Thumbnails are generated by the same functions that render the
            // real page, so what you pick is what you get.
            const layers = backgroundLayers(id, palette, seed, '')
            const scrim = scrimLayers(id, palette)
            const blend = backgroundBlend(id)
            return (
              <button
                key={id}
                type="button"
                className="swatch"
                aria-pressed={value.background === id}
                aria-label={BACKGROUNDS[id].label}
                title={BACKGROUNDS[id].label}
                onClick={() => onChange({ ...value, background: id })}
              >
                <span
                  className="pal"
                  style={{
                    backgroundColor: palette.bg,
                    backgroundImage: [scrim, layers].filter((l) => l && l !== 'none').join(','),
                    // The scrim is layer 0, so prepend a normal blend to keep
                    // the photo modes aligned with their layers.
                    backgroundBlendMode: blend === 'normal' ? 'normal' : `normal,${blend}`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                />
                <span className="pal-name">{BACKGROUNDS[id].label}</span>
              </button>
            )
          })}
        </div>
      </fieldset>
    </>
  )
}
